<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PageHero from '@/components/common/PageHero.vue'
import StockFilterPanel from '@/components/stock/StockFilterPanel.vue'
import BasePagination from '@/components/common/BasePagination.vue'
import { useTableSort } from '@/composables/useTableSort'
import { getTradingStrategyPool } from '@/api/tradingStrategy'
import {
  formatAmount,
  formatDate,
  formatNumber,
  formatPctChg,
  formatTurnover,
  formatVolume,
  getPriceClass
} from '@/utils/formatters'

const route = useRoute()
const router = useRouter()

const streakGroups = [
  { value: 0, label: '最终形态', desc: '四层过滤后的全部候选' },
  { value: 2, label: '2连阳', desc: '连续 2 天收盘 ≥ 开盘' },
  { value: 3, label: '3连阳', desc: '连续 3 天收盘 ≥ 开盘' },
  { value: 4, label: '4连阳', desc: '连续 4 天收盘 ≥ 开盘' },
  { value: 5, label: '更高', desc: '连续 5 天及以上' }
]

const marketColumns = [
  { key: 'rank', label: '排名', align: 'center', width: 70 },
  { key: 'symbol', label: '代码', align: 'left', width: 82 },
  { key: 'name', label: '名称', align: 'left', width: 82 },
  { key: 'trade_date', label: '交易日', align: 'center', width: 94 },
  { key: 'open', label: '开盘', width: 64 },
  { key: 'high', label: '最高', width: 64 },
  { key: 'low', label: '最低', width: 64 },
  { key: 'close', label: '收盘', width: 68 },
  { key: 'price_change', label: '涨跌额', width: 70 },
  { key: 'pct_chg', label: '涨跌幅', width: 76 },
  { key: 'amplitude', label: '振幅', width: 70 },
  { key: 'volume', label: '成交量', width: 80 },
  { key: 'amount', label: '成交额', width: 84 },
  { key: 'turnover', label: '换手', width: 64 }
]
const columns = [
  ...marketColumns,
  { key: 'ma3', label: 'MA3', width: 66 },
  { key: 'ma5', label: 'MA5', width: 66 },
  { key: 'ma6', label: 'MA6', width: 66 },
  { key: 'ma9', label: 'MA9', width: 66 },
  { key: 'prev_pct_chg', label: '前日涨幅', width: 76 },
  { key: 'volume_ratio', label: '量比', width: 62 }
]
const tableMinWidth = `${columns.reduce((sum, column) => sum + column.width, 0)}px`

function parseStreak (value) {
  const streak = Number(value)
  return [0, 2, 3, 4, 5].includes(streak) ? streak : 0
}

function streakKey (value) {
  return value === 0 ? 'all' : String(value)
}

const activeStreak = ref(parseStreak(route.query.streak))
const filterForm = ref({
  tradeDate: String(route.query.trade_date || ''),
  symbol: String(route.query.symbol || '')
})
const rows = ref([])
const bullCounts = ref({ all: null, 2: null, 3: null, 4: null, 5: null })
const topPicks = ref([])
const total = ref(0)
const resultDate = ref('')
const prevTradeDate = ref('')
const pageNum = ref(Math.max(1, Number(route.query.page) || 1))
const pageSize = ref(10)
const loading = ref(false)
const error = ref('')
let requestId = 0
let sizeChanging = false

const { sortField, sortOrder, handleSort, resetSort } = useTableSort({
  defaultField: 'rank',
  defaultOrder: 'asc',
  getDefaultOrder: field => ['symbol', 'name', 'trade_date', 'rank'].includes(field) ? 'asc' : 'desc',
  onChange: () => {
    pageNum.value = 1
    load()
  }
})

const activeStreakMeta = computed(() => streakGroups.find(group => group.value === activeStreak.value))
const activeCount = computed(() => bullCounts.value[streakKey(activeStreak.value)])
const paddedRows = computed(() => {
  const list = [...rows.value]
  while (list.length < 10) list.push(null)
  return list
})

const tabRefs = ref([])
const indicatorStyle = ref({ width: '0px', transform: 'translateX(0px)' })

function updateIndicator () {
  const index = streakGroups.findIndex(group => group.value === activeStreak.value)
  const element = tabRefs.value[index]
  if (element) {
    indicatorStyle.value = {
      width: `${element.offsetWidth}px`,
      transform: `translateX(${element.offsetLeft}px)`
    }
  }
}

function setTabRef (element, index) {
  if (element) tabRefs.value[index] = element
}

watch(activeStreak, () => nextTick(updateIndicator))
watch(bullCounts, counts => {
  if (counts[streakKey(activeStreak.value)]) return
  const first = streakGroups.find(group => counts[streakKey(group.value)] > 0)
  if (first) {
    activeStreak.value = first.value
    pageNum.value = 1
    load()
  }
}, { deep: true })

function cellText (item, key) {
  switch (key) {
    case 'symbol': return item.symbol
    case 'name': return item.name
    case 'trade_date': return formatDate(item.trade_date)
    case 'open':
    case 'high':
    case 'low':
    case 'close': return formatNumber(item[key])
    case 'price_change': return `${item[key] > 0 ? '+' : ''}${formatNumber(item[key])}`
    case 'pct_chg': return formatPctChg(item.pct_chg)
    case 'amplitude': return item.amplitude == null ? '—' : `${formatNumber(item.amplitude)}%`
    case 'volume': return formatVolume(item.volume)
    case 'amount': return formatAmount(item.amount)
    case 'turnover': return formatTurnover(item.turnover)
    case 'ma3':
    case 'ma5':
    case 'ma6':
    case 'ma9': return formatNumber(item[key], 3)
    case 'prev_pct_chg': return formatPctChg(item.prev_pct_chg)
    case 'volume_ratio': return item.volume_ratio == null ? '—' : `${formatNumber(item.volume_ratio)}×`
    default: return '—'
  }
}

function pctCellClass (value) {
  if (value == null) return ''
  return value > 0 ? 'cell-up' : value < 0 ? 'cell-down' : ''
}

function cellClass (item, key) {
  if (key === 'pct_chg') return ['pct-cell', pctCellClass(item.pct_chg)]
  if (['open', 'high', 'low', 'close', 'price_change'].includes(key)) {
    return getPriceClass(item[key], item.pct_chg)
  }
  if (key === 'prev_pct_chg') return getPriceClass(item.prev_pct_chg, item.prev_pct_chg)
  return ''
}

function streakCount (value) {
  const count = bullCounts.value[streakKey(value)]
  return count == null ? '—' : count.toLocaleString('zh-CN')
}

function errorMessage (err) {
  const status = err?.response?.status
  const detail = err?.response?.data?.message || err?.response?.data?.detail
  if (typeof detail === 'string' && detail) return detail
  if (status === 500) return '数据服务暂时不可用，请确认数据库连接后重试'
  if (status === 401) return '登录已失效，请重新登录'
  return err?.message || '查询失败'
}

async function load () {
  const current = ++requestId
  loading.value = true
  error.value = ''
  try {
    const data = await getTradingStrategyPool({
      tradeDate: filterForm.value.tradeDate,
      layer: 3,
      streak: activeStreak.value,
      symbol: filterForm.value.symbol.trim(),
      pageNum: pageNum.value,
      pageSize: pageSize.value,
      sortField: sortField.value,
      sortOrder: sortOrder.value
    })
    if (current !== requestId) return
    rows.value = data?.list || []
    total.value = data?.total || 0
    bullCounts.value = data?.bull_counts || bullCounts.value
    topPicks.value = data?.top_picks || []
    resultDate.value = data?.trade_date || ''
    prevTradeDate.value = data?.prev_trade_date || ''
    if (data?.trade_date) filterForm.value.tradeDate = data.trade_date
    if (data?.page_num) pageNum.value = data.page_num
    syncQuery()
  } catch (err) {
    if (current !== requestId) return
    error.value = errorMessage(err)
    rows.value = []
    total.value = 0
  } finally {
    if (current === requestId) loading.value = false
  }
}

function syncQuery () {
  const next = { ...route.query }
  if (filterForm.value.tradeDate) next.trade_date = filterForm.value.tradeDate
  else delete next.trade_date
  const symbol = filterForm.value.symbol.trim()
  if (symbol) next.symbol = symbol
  else delete next.symbol
  if (activeStreak.value === 0) delete next.streak
  else next.streak = String(activeStreak.value)
  if (pageNum.value > 1) next.page = String(pageNum.value)
  else delete next.page
  delete next.layer

  const changed = ['trade_date', 'symbol', 'streak', 'page', 'layer']
    .some(key => String(next[key] || '') !== String(route.query[key] || ''))
  if (changed) router.replace({ query: next })
}

function selectStreak (value) {
  if (value === activeStreak.value) return
  activeStreak.value = value
  pageNum.value = 1
  load()
}

function search () {
  pageNum.value = 1
  load()
}

function reset () {
  filterForm.value = { tradeDate: '', symbol: '' }
  activeStreak.value = 0
  pageNum.value = 1
  resetSort()
  load()
}

function changePage (page) {
  if (sizeChanging) return
  pageNum.value = page
  load()
}

function changePageSize (size) {
  sizeChanging = true
  pageSize.value = size
  pageNum.value = 1
  load().finally(() => {
    sizeChanging = false
  })
}

function openStock (item) {
  if (!item) return
  router.push({ name: 'stock-kline', params: { symbol: item.symbol }, query: { name: item.name } })
}

onMounted(async () => {
  await load()
  nextTick(updateIndicator)
})
</script>

<template>
  <div class="strategy-page page">
    <PageHero>
      <template #actions>
        <div v-if="topPicks.length" class="default-picks">
          <span class="picks-label">默认买入</span>
          <button
            v-for="pick in topPicks"
            :key="pick.symbol"
            type="button"
            class="pick"
            :title="`${pick.name} ${pick.symbol} · 评分 ${pick.score}`"
            @click="openStock(pick)"
          >
            <span class="pick-rank">{{ pick.rank }}</span>
            <span class="pick-body">
              <span class="pick-name">{{ pick.name }}</span>
              <span class="pick-code">{{ pick.symbol }}</span>
            </span>
            <span class="pick-score">{{ pick.score }}</span>
          </button>
        </div>
      </template>
    </PageHero>

    <StockFilterPanel
      v-model="filterForm"
      :show-turnover="false"
      :show-amount="false"
      :show-board-filters="false"
      @search="search"
      @reset="reset"
    />

    <div class="group-section">
      <div class="tab-slider">
        <div class="tab-list" role="tablist" aria-label="连续阳线分组">
          <div
            v-for="(group, index) in streakGroups"
            :key="group.value"
            class="tab-item"
            :class="{ active: activeStreak === group.value, empty: !bullCounts[streakKey(group.value)] }"
            role="tab"
            :tabindex="bullCounts[streakKey(group.value)] ? 0 : -1"
            :aria-selected="activeStreak === group.value"
            :ref="element => setTabRef(element, index)"
            @click="bullCounts[streakKey(group.value)] ? selectStreak(group.value) : null"
            @keydown.enter="bullCounts[streakKey(group.value)] ? selectStreak(group.value) : null"
          >
            <span class="tab-main">
              <span class="tab-label">{{ group.label }}</span>
              <span class="tab-count">{{ streakCount(group.value) }}</span>
            </span>
            <span class="tab-desc">{{ group.desc }}</span>
          </div>
          <div class="tab-indicator" :style="indicatorStyle" />
        </div>
      </div>

      <div class="group-meta">
        <span v-if="resultDate">
          {{ resultDate }} 收盘后 · 最终形态 · {{ activeStreakMeta.desc }}
        </span>
        <span v-else>最终形态 · 点击分组按连续阳线天数筛选</span>
        <span class="group-total">
          {{ total.toLocaleString('zh-CN') }} 只
          <span v-if="filterForm.symbol.trim() && activeCount != null"> / 分组 {{ activeCount.toLocaleString('zh-CN') }} 只</span>
          <span v-if="prevTradeDate"> · 前一交易日 {{ prevTradeDate }}</span>
        </span>
      </div>

      <div class="table-section">
        <div v-if="loading" class="state loading">正在筛选…</div>
        <div v-else-if="error" class="state error">
          <p>{{ error }}</p>
          <button class="btn-ghost" @click="load">重新查询</button>
        </div>
        <div v-else-if="!total" class="state empty">
          <p>{{ activeCount === 0 ? '该分组当前没有股票' : '该条件下暂无候选股票' }}</p>
          <p v-if="filterForm.symbol.trim()" class="state-hint">试试清空代码筛选，或切换到其它分组。</p>
        </div>
        <table v-else class="stock-table strategy-table" :style="{ minWidth: tableMinWidth }">
          <colgroup>
            <col v-for="column in columns" :key="column.key" :style="{ width: `${column.width}px` }" />
          </colgroup>
          <thead>
            <tr>
              <th
                v-for="column in columns"
                :key="column.key"
                class="sortable"
                :class="column.align ? `th-${column.align}` : ''"
                :title="column.key === 'rank' ? '综合评分：连阳 30% + 量比 25% + 成交额 20% + 涨幅 15% + 换手 10%' : ''"
                @click="handleSort(column.key)"
              >
                <span class="th-label">{{ column.label }}</span>
                <span class="sort-icon">{{ sortField === column.key ? (sortOrder === 'asc' ? '▲' : '▼') : '⇅' }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in paddedRows"
              :key="item?.symbol || `empty-${index}`"
              :class="{ 'empty-row': !item, 'top-pick-row': item && item.rank <= 3 }"
              @dblclick="openStock(item)"
            >
              <template v-if="item">
                <td
                  v-for="column in columns"
                  :key="column.key"
                  :class="[column.align === 'left' ? 'td-left' : column.align === 'center' ? 'td-center' : '', cellClass(item, column.key)]"
                >
                  <template v-if="column.key === 'rank'">
                    <span class="rank-num" :class="{ 'rank-top': item.rank <= 3 }">{{ item.rank }}</span>
                    <span class="rank-score">{{ item.score }}</span>
                  </template>
                  <template v-else>{{ cellText(item, column.key) }}</template>
                </td>
              </template>
              <template v-else>
                <td v-for="column in columns" :key="column.key">&nbsp;</td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>

      <BasePagination
        v-if="total > 0"
        :page-num="pageNum"
        :page-size="pageSize"
        :total="total"
        @update:page-num="changePage"
        @update:page-size="changePageSize"
      />
      <p class="method-note">排名按整层综合评分（连阳 30% · 量比 25% · 成交额 20% · 涨幅 15% · 换手 10%），组内按评分重新编号，默认买入前 3 只；点击分组按连续阳线天数筛选。规则使用未复权收盘价，历史非 ST 判断采用当前名称。2026-01—10 回测显示：评分只在 7—8 月跑赢随机，跨区间不稳定；更高组 + 严格 T+1 是唯一三个子区间都为正的组合，但收益集中在少数交易。名单供收盘后观察，不代表当日可成交。</p>
    </div>
  </div>
</template>

<style scoped lang="scss">
.strategy-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 4px 28px 12px;
  box-sizing: border-box;
  overflow: hidden;
}

.group-section {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.tab-slider {
  flex-shrink: 0;
  padding: 4px 0 0;
  border-bottom: 1px solid var(--rule);
}

.tab-list {
  position: relative;
  display: flex;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.tab-item {
  flex: 0 0 auto;
  min-width: 150px;
  padding: 9px 20px 11px;
  cursor: pointer;
  user-select: none;
  transition: color 0.2s;
  position: relative;

  &.empty {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:not(.empty):hover {
    color: var(--text-primary);
  }

  &.active {
    color: var(--accent);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: -2px;
  }
}

.tab-main {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.tab-label {
  font-family: var(--font-mono);
  font-size: 14px;
  font-weight: 700;
}

.tab-count {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 10px;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
}

.tab-item.active .tab-count {
  background: var(--accent);
  color: white;
}

.tab-desc {
  display: block;
  margin-top: 2px;
  font-size: 11px;
  color: var(--text-muted);
  white-space: nowrap;
}

.tab-indicator {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  background: var(--accent);
  border-radius: 2px 2px 0 0;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.group-meta {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
  padding: 7px 2px;
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--text-muted);

  .group-total {
    flex-shrink: 0;
    color: var(--text-secondary);
  }
}

.default-picks {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  .picks-label {
    font-family: var(--font-mono);
    font-size: 9px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--text-faint);
  }

  .pick {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 5px 8px;
    color: var(--text-secondary);
    background: transparent;
    border: 1px solid var(--rule);
    border-radius: 4px;
    cursor: pointer;
    font-family: inherit;
    transition: border-color 0.18s, color 0.18s, background 0.18s;

    &:hover {
      border-color: var(--text-faint);
      color: var(--text-primary);
      background: var(--bg-secondary);
    }
  }

  .pick-rank {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--accent);
    color: white;
    font-family: var(--font-mono);
    font-size: 9px;
    font-weight: 600;
    flex-shrink: 0;
  }

  .pick-body {
    display: flex;
    flex-direction: column;
    gap: 1px;
    text-align: left;
    min-width: 0;
  }

  .pick-name {
    font-size: 12px;
    line-height: 1.1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 76px;
  }

  .pick-code {
    font-family: var(--font-mono);
    font-size: 9px;
    line-height: 1;
    color: var(--text-faint);
  }

  .pick-score {
    font-family: var(--font-mono);
    font-size: 10px;
    color: var(--accent);
    font-variant-numeric: tabular-nums;
  }
}

.rank-num {
  display: block;
  font-family: var(--font-mono);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.1;
  color: var(--text-secondary);

  &.rank-top {
    color: var(--accent);
  }
}

.rank-score {
  display: block;
  font-family: var(--font-mono);
  font-size: 9px;
  line-height: 1.1;
  color: var(--text-faint);
}

.strategy-table tbody tr.top-pick-row {
  background: var(--accent-subtle);

  &:hover {
    background: var(--accent-subtle);
  }
}

.table-section {
  min-height: 0;
}

.strategy-table {
  font-size: 13px;

  th,
  td {
    padding: 6px 8px;
  }

  th {
    font-size: 11px;
    letter-spacing: 0.06em;
  }

  tbody tr {
    height: 45px;
  }

  .pct-cell.cell-up {
    background: var(--up-bg);
    color: var(--up);
  }

  .pct-cell.cell-down {
    background: var(--down-bg);
    color: var(--down);
  }

  td.up {
    color: var(--up);
  }

  td.down {
    color: var(--down);
  }
}

.state {
  p {
    margin: 0;
  }

  p + p {
    margin-top: 8px;
  }

  .state-hint {
    font-size: 11px;
    color: var(--text-faint);
  }

  &.error {
    color: var(--up);
  }
}

.btn-ghost {
  margin-top: 12px;
  padding: 6px 14px;
  color: var(--text-secondary);
  background: transparent;
  border: 1px solid var(--rule);
  border-radius: 4px;
  cursor: pointer;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.08em;
  transition: all 0.2s;

  &:hover {
    border-color: var(--text-faint);
    color: var(--text-primary);
  }
}

.method-note {
  flex-shrink: 0;
  margin: 5px 14px 0;
  font-size: 10px;
  line-height: 1.4;
  color: var(--text-faint);
}

@media (max-width: 1500px) {
  .default-picks {
    .pick {
      padding: 4px 6px;
    }

    .pick-name {
      max-width: 56px;
    }

    .pick-score {
      display: none;
    }
  }
}

@media (max-width: 768px) {
  .strategy-page {
    height: auto;
    min-height: 100%;
    padding: 4px 16px 12px;
    overflow: visible;
  }

  .group-section {
    overflow: visible;
  }

  .tab-list {
    scrollbar-width: auto;

    &::-webkit-scrollbar {
      display: block;
      height: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--text-faint);
      border-radius: 2px;
    }
  }

  .tab-item {
    min-width: 132px;
    padding: 8px 14px 10px;
  }

  .tab-desc {
    font-size: 10px;
  }

  .group-meta {
    flex-direction: column;
    gap: 3px;
  }
}
</style>
